import { useCallback, useEffect, useState } from 'react'

import { get, isEmpty, isEqual, uniq } from 'lodash-es'

import { useGenAiHighRiskFlow } from './useGenAiHighRiskFlow'
import { useRemoveMatchedPreference } from './useRemoveMatchedPreference'
import { GenAiRiskLevel, MEETING_FORMATS, ParsePreferenceRequest, ParsePreferenceResponse } from '../../../models'
import { PRODUCT_TYPE } from '../../common/constants/productType'
import { isValidCustomPreferencesTextInput } from '../../common/utils/validationUtils'
import {
  emptyProviderPreferenceState,
  MEMBER_PREFERENCE_IDS,
  ProviderPreferenceValues,
  S4C_PROGRAM_OPTION_FLOW,
} from '../../memberPreferences/constants'
// Metadata imports -- long term consider migrating to the BE when we set up a MP service to fetch and serve these files
import { PreferenceMetadataConfig, RENDER_CONDITION_TARGETS, RenderCondition } from '../../memberPreferences/metadata'
import { getAvailableMemberPreferenceListOptions } from '../../memberPreferences/metadata/configuration'
import { MemberPreferencesForUserV2, ParsedPreference, ParsedPreferenceKey } from '../../memberPreferences/types'
import {
  filterMemberPreferencesOptions,
  getBipocUserPreferenceSelection,
  getInitialProviderPreferenceValues,
  getIsValidSearchForModalityPreferenceForMemberPreferences,
  getModalityPreferenceProgramOption,
  matchMeetingSettingFormatToMemberPreferencesValues,
  removePreferenceValueFromParsedPreferences,
  setMeetingFormatForUpdatedCoachingPreferences,
} from '../../memberPreferences/utils'

export interface useMemberPreferencesArgs {
  memberPreferencesV2: MemberPreferencesForUserV2 | null
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  shouldShowMeetingStylePreferenceModal: boolean
  isSearchingForCoaching: boolean
  isSearchingForAdultTherapy: boolean
  postModalityPreferenceData: ({
    lyraId,
    searchId,
    data,
  }: {
    lyraId: string
    searchId: string
    data: { modalityPreferences: string[] }
  }) => Promise<any>
  userId: string
  selectedSearchId?: string | null
  setMeetingFormat: (value: string) => void
  isParentInitiatedInPersonS4CPreferenceChecked: boolean
  isParentInitiatedInPersonTeenS4CPreferenceChecked: boolean
  setIsParentInitiatedInPersonS4CPreferenceChecked: (value: boolean) => void
  setIsParentInitiatedInPersonTeenS4CPreferenceChecked: (value: boolean) => void
  saveMemberPreferenceForUserV2: ({
    lyraId,
    data,
  }: {
    lyraId: string
    data: MemberPreferencesForUserV2
  }) => Promise<any>
  setUserMemberPreferencesV2?: (value: MemberPreferencesForUserV2) => void // Mobile only, web fx is built into saveMemberPreferenceForUserV2
  onProviderPreferenceChangeFail: (err: any) => void
  shouldShowAvailabilityPreference: boolean
  timeZone: string
  userCountryIsoCode?: string
  productType: PRODUCT_TYPE
  areExpandedGlobalPreferencesEnabled?: boolean
  onConfirmPreferences?: () => void
  isCustomPreferencesEnabled?: boolean
  /** Thunk that calls the GenAI preference parsing API and dispatches result to Redux */
  postParsePreference?: ({
    lyraId,
    data,
  }: {
    lyraId: string
    data: ParsePreferenceRequest
  }) => Promise<ParsePreferenceResponse>
  /** Redux state: parsed preference object containing data, id, and riskLevel from API response */
  parsePreference?: { data: ParsedPreference; id: string | null; riskLevel: GenAiRiskLevel | null }
  /** Redux action: update parse preference data (for removing individual preferences) */
  setParsePreferenceData?: (data: {
    data: ParsedPreference
    id: string | null
    riskLevel: GenAiRiskLevel | null
  }) => void
}

/** Props grouped for the CustomPreferencesModal component */
export interface CustomPreferencesModalHookProps {
  isVisible: boolean
  onCancel: () => void
  onSubmit: () => void | Promise<void>
  matchedCustomPreferences: ParsedPreference
  isCustomPreferencesLoading: boolean
  isCustomPreferencesError: boolean
  onMeetMatches: () => void
  onRemoveParsedPreference: ({
    preferenceKey,
    valueId,
  }: {
    preferenceKey: ParsedPreferenceKey
    valueId: string | undefined
  }) => void
  viewMatchedPreferences: boolean
  onBackToInput: () => void
  isHighRiskModalVisible: boolean
  onDismissHighRiskModal: () => void
  isHighRisk: boolean
  hasAcknowledgedHighRisk: boolean
}

// A hook that takes in the redux state and actions from both web / mobile Preferences and generates the
// props needed for the provider preference selection page
export const useMemberPreferences = ({
  memberPreferencesV2,
  searchForCareProgramOptionFlow,
  shouldShowMeetingStylePreferenceModal,
  isSearchingForCoaching,
  isSearchingForAdultTherapy,
  postModalityPreferenceData,
  userId,
  selectedSearchId,
  setMeetingFormat,
  isParentInitiatedInPersonS4CPreferenceChecked,
  isParentInitiatedInPersonTeenS4CPreferenceChecked,
  setIsParentInitiatedInPersonS4CPreferenceChecked,
  setIsParentInitiatedInPersonTeenS4CPreferenceChecked,
  saveMemberPreferenceForUserV2,
  setUserMemberPreferencesV2,
  onProviderPreferenceChangeFail,
  shouldShowAvailabilityPreference = false,
  timeZone,
  userCountryIsoCode,
  productType,
  areExpandedGlobalPreferencesEnabled = false,
  onConfirmPreferences,
  isCustomPreferencesEnabled = false,
  postParsePreference,
  parsePreference,
  setParsePreferenceData,
}: useMemberPreferencesArgs) => {
  // Derive values from parsePreference object
  const matchedCustomPreferences = parsePreference?.data ?? {}
  const parsePreferenceId = parsePreference?.id ?? null
  const isHighRisk = parsePreference?.riskLevel === GenAiRiskLevel.RISK_LEVEL_YELLOW
  const isValidSearchForModalityPreferenceForMemberPreferences =
    getIsValidSearchForModalityPreferenceForMemberPreferences({
      searchForCareProgramOptionFlow,
      userCountryIsoCode,
    })
  const isCoachingWithLiveMessagingSearch = shouldShowMeetingStylePreferenceModal && isSearchingForCoaching

  // Check if parent child search aged 0-12
  const isSearchingMemberPreferencesTreatmentOptionForChildTherapy =
    searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD
  // Check if parent child search aged 13-17
  const isSearchingMemberPreferencesTreatmentOptionForTeenTherapy =
    searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN

  // Determines which meeting setting preference user sees, if any
  const modalityPreferenceOption = getModalityPreferenceProgramOption({
    shouldShowMeetingStylePreferenceModal,
    isSelectedTreatmentOptionCoaching: isSearchingForCoaching,
  })

  /* State maintained by this hook for usage in web / mobile containers */
  const [memberPreferencesMetadata, setMemberPreferencesMetadata] = useState<PreferenceMetadataConfig[]>([])

  // Submitting state for updating selected preferences
  const [isSavingPreferences, setIsSavingPreferences] = useState(false)

  const [currentPreferences, setCurrentPreferences] = useState<ProviderPreferenceValues>(
    getInitialProviderPreferenceValues({
      memberPreferencesV2,
      isValidSearchForModalityPreferenceForMemberPreferences,
      isCoachingWithLiveMessagingSearch,
      shouldShowAvailabilityPreference,
      isCustomPreferencesEnabled,
    }),
  )

  // Determine empty state based on feature flags
  const getEmptyPreferenceState = ({
    shouldShowAvailability,
    isCustomPreferencesEnabled,
  }: {
    shouldShowAvailability: boolean
    isCustomPreferencesEnabled: boolean
  }) => ({
    ...emptyProviderPreferenceState,
    ...(shouldShowAvailability && { availabilities: null }),
    ...(isCustomPreferencesEnabled && { customFreeTextPreference: '' }),
  })

  const [isResetDisabled, setResetDisabled] = useState(
    isEqual(
      currentPreferences,
      getEmptyPreferenceState({
        shouldShowAvailability: shouldShowAvailabilityPreference,
        isCustomPreferencesEnabled: isCustomPreferencesEnabled,
      }),
    ),
  )

  const [isHowPreferencesWorkModalVisible, setIsHowPreferencesWorkModalVisible] = useState(false)

  // Custom preferences modal state
  const [isCustomPreferencesModalVisible, setIsCustomPreferencesModalVisible] = useState(false)

  // Function to iterate through render conditions as another check before rendering preference accordion
  const getRenderConditionStatus = useCallback(
    ({
      renderConditions,
      preferenceName,
    }: {
      renderConditions: RenderCondition[] | undefined
      preferenceName: string
    }) => {
      const renderConditionFeatureFlagTargetMapping = {
        availabilities: shouldShowAvailabilityPreference,
      }
      if (!renderConditions) return true
      return renderConditions.every((condition) => {
        let target: any = undefined
        switch (condition.target) {
          case RENDER_CONDITION_TARGETS.FEATURE_FLAG:
            target = renderConditionFeatureFlagTargetMapping.hasOwnProperty(preferenceName)
              ? renderConditionFeatureFlagTargetMapping[preferenceName]
              : true
            break
          case RENDER_CONDITION_TARGETS.PRODUCT_TYPE:
            target = productType
            break
          default:
            console.error(`Unrecognized render condition target: ${condition.target}`)
            break
        }
        return condition.fn(target)
      })
    },
    [shouldShowAvailabilityPreference, productType],
  )

  useEffect(() => {
    const parseMemberPreferencesMetadata = ({
      fetchedPreferencesList,
    }: {
      fetchedPreferencesList: PreferenceMetadataConfig[]
    }) => {
      const parsedMetadataArray = fetchedPreferencesList
        .map((preferenceConfig: PreferenceMetadataConfig) => {
          // If this accordion is not visible for the current searchForCareProgramOptionFlow, skip it
          if (
            !get(
              preferenceConfig,
              `countryConfig.${userCountryIsoCode}.programOptionFlows`,
              [] as S4C_PROGRAM_OPTION_FLOW[],
            ).includes(searchForCareProgramOptionFlow)
          )
            return null

          // Check if renderConditions exists for current preference config
          if (
            !getRenderConditionStatus({
              renderConditions: preferenceConfig.renderConditions,
              preferenceName: preferenceConfig.name,
            })
          ) {
            return null
          }
          return preferenceConfig
        })
        .filter((metadata): metadata is PreferenceMetadataConfig => metadata !== null)

      setMemberPreferencesMetadata(parsedMetadataArray)
    }

    const availableMemberPreferenceListOptions = getAvailableMemberPreferenceListOptions({
      isUserInternational: !!userCountryIsoCode && userCountryIsoCode !== 'US',
      areExpandedGlobalPreferencesEnabled,
    })
    parseMemberPreferencesMetadata({ fetchedPreferencesList: availableMemberPreferenceListOptions })
  }, [
    searchForCareProgramOptionFlow,
    modalityPreferenceOption,
    setMemberPreferencesMetadata,
    getRenderConditionStatus,
    userCountryIsoCode,
    areExpandedGlobalPreferencesEnabled,
  ])

  const onPressResetButton = () => {
    if (isSearchingMemberPreferencesTreatmentOptionForChildTherapy) {
      setIsParentInitiatedInPersonS4CPreferenceChecked(false)
    }
  }

  // Whenever a user selects and saves any new specific preference, we re-create the preferences blob with ALL preferences
  // to update the store and save the preferences in the backend so that the user won't have to refill preferences during their next search
  const onProviderPreferenceChange = async (newPreferenceValues: ProviderPreferenceValues) => {
    setCurrentPreferences(newPreferenceValues)
    try {
      setIsSavingPreferences(true)

      const filteredEthnicities = filterMemberPreferencesOptions({
        preferenceCategory: newPreferenceValues.ethnicities,
        shouldFilterMemberPreferencesOptions: !isSearchingForAdultTherapy,
        filterOptions: [MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN],
      })

      const updateModalityPreferenceTherapyOption =
        isValidSearchForModalityPreferenceForMemberPreferences &&
        !isEqual(memberPreferencesV2?.preferredAppointmentType, newPreferenceValues.meetingSetting)

      const isLgbtqia = newPreferenceValues.isLgbtqia.length ? true : false
      const isBipoc = getBipocUserPreferenceSelection({ userPreferenceSelections: filteredEthnicities })

      // If the user is searching for therapy and the modality preference has been changed, update
      // the modality preference in the BE
      if (updateModalityPreferenceTherapyOption) {
        const isInPersonSelected = newPreferenceValues.meetingSetting.some(
          (val) => val === MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS,
        )
        await postModalityPreferenceData({
          lyraId: userId,
          searchId: selectedSearchId || '',
          data: { modalityPreferences: isInPersonSelected ? [MEETING_FORMATS.IN_PERSON] : [MEETING_FORMATS.VIDEO] },
        })
      } else if (isSearchingForCoaching) {
        const updatedValues = {
          [MEETING_FORMATS.LIVE_MESSAGING]: newPreferenceValues.meetingSetting.some(
            (val) => val === MEETING_FORMATS.LIVE_MESSAGING,
          ),
          [MEETING_FORMATS.VIDEO]: newPreferenceValues.meetingSetting.some((val) => val === MEETING_FORMATS.VIDEO),
        }

        setMeetingFormatForUpdatedCoachingPreferences({
          setMeetingFormatFn: setMeetingFormat,
          updatedValues,
        })
      }
      // Prevent in-person option from being checked when the user returns to the member preferences page
      if (
        isSearchingMemberPreferencesTreatmentOptionForChildTherapy &&
        isParentInitiatedInPersonS4CPreferenceChecked &&
        newPreferenceValues.meetingSetting.length === 0
      ) {
        setIsParentInitiatedInPersonS4CPreferenceChecked(false)
      } else if (
        isSearchingMemberPreferencesTreatmentOptionForTeenTherapy &&
        isParentInitiatedInPersonTeenS4CPreferenceChecked &&
        newPreferenceValues.meetingSetting.length > 0
      ) {
        setIsParentInitiatedInPersonTeenS4CPreferenceChecked(false)
      }

      const newPreferences = {
        ethnicities: newPreferenceValues.ethnicities,
        gender: newPreferenceValues.gender,
        isBipoc,
        isLgbtqia,
        religions: newPreferenceValues.religions,
        isExperiencedWithVeterans: newPreferenceValues.isExperiencedWithVeterans.length ? true : false,
        preferredAppointmentType: isValidSearchForModalityPreferenceForMemberPreferences
          ? matchMeetingSettingFormatToMemberPreferencesValues({ meetingSetting: newPreferenceValues.meetingSetting })
          : memberPreferencesV2?.preferredAppointmentType || [],
        preferredSessionFormat: isSearchingForCoaching
          ? matchMeetingSettingFormatToMemberPreferencesValues({ meetingSetting: newPreferenceValues.meetingSetting })
          : memberPreferencesV2?.preferredSessionFormat || [],
        ...(shouldShowAvailabilityPreference && {
          availabilities: newPreferenceValues?.availabilities,
          timeZone,
        }),
        ...(isCustomPreferencesEnabled && {
          customFreeTextPreference: newPreferenceValues.customFreeTextPreference ?? '',
        }),
        ...(parsePreferenceId !== undefined && parsePreferenceId !== null && { parsePreferenceId }),
      }

      // Send updated preferences to BE to be saved in user_property table with isBipoc value
      await saveMemberPreferenceForUserV2({
        lyraId: userId,
        data: newPreferences,
      })
      // web version sets the user member preferences in store as apart of 'saveMemberPreferencesForUserV2'
      // mobile does it separately and will pass this fx below
      if (setUserMemberPreferencesV2) {
        // Update redux store with new preferences
        setUserMemberPreferencesV2(newPreferences)
      }
    } catch (err) {
      onProviderPreferenceChangeFail(err)
    } finally {
      setIsSavingPreferences(false)
    }
  }

  // Local loading state for custom preferences API call
  const [isCustomPreferencesLoading, setIsCustomPreferencesLoading] = useState(false)
  // Local error state for custom preferences API call
  const [isCustomPreferencesError, setIsCustomPreferencesError] = useState(false)
  // Tracks whether we're viewing results (stays true even if user removes all preferences)
  const [viewMatchedPreferences, setViewMatchedPreferences] = useState(false)

  // GenAI high risk flow management - extracted to dedicated hook
  const {
    isGenAiHighRiskModalVisible: isHighRiskModalVisible,
    isReopeningAfterGenAiHighRisk: isReopeningAfterHighRisk,
    hasAcknowledgedHighRisk,
    onGenAiHighRiskDetected,
    onDismissGenAiHighRiskModal: onDismissHighRiskModal,
  } = useGenAiHighRiskFlow({
    isSearchingForCoaching,
    isCustomPreferencesModalVisible,
    onReopenCustomPreferencesModal: () => setIsCustomPreferencesModalVisible(true),
    onPrepareResultsView: () => setViewMatchedPreferences(true),
    onCloseCustomPreferencesModal: () => {
      setIsCustomPreferencesModalVisible(false)
      setViewMatchedPreferences(false) // Reset to input state when closing
    },
  })

  const onSubmitCustomPreferences = async () => {
    // If custom text is empty, close modal and skip API call
    if (!currentPreferences.customFreeTextPreference) {
      setViewMatchedPreferences(false)
      setIsCustomPreferencesModalVisible(false)
      return
    }
    // Validate custom preferences text input - UI handles error display, but we still prevent API call
    if (!isValidCustomPreferencesTextInput(currentPreferences.customFreeTextPreference)) {
      return
    }
    if (!postParsePreference || !selectedSearchId) {
      return
    }

    setIsCustomPreferencesLoading(true)
    setIsCustomPreferencesError(false)

    const response = await postParsePreference({
      lyraId: userId,
      data: {
        searchId: selectedSearchId,
        rawFreeText: currentPreferences.customFreeTextPreference,
      },
    }).catch(() => {
      setIsCustomPreferencesLoading(false)
      setIsCustomPreferencesError(true)
      setViewMatchedPreferences(true)
      return null
    })

    if (!response) {
      return
    }

    const { preferences: parsedPrefs, riskLevel } = response

    // Auto-select preferences in accordions based on parsed preferences
    if (!isEmpty(parsedPrefs)) {
      const updatedPreferences: ProviderPreferenceValues = {
        ...currentPreferences,
        gender: uniq([...currentPreferences.gender, ...(parsedPrefs.gender ?? [])]),
        ethnicities: uniq([...currentPreferences.ethnicities, ...(parsedPrefs.ethnicities ?? [])]),
        religions: uniq([...currentPreferences.religions, ...(parsedPrefs.religions ?? [])]),
        availabilities:
          uniq([...(currentPreferences.availabilities ?? []), ...(parsedPrefs.availabilities ?? [])]).length > 0
            ? uniq([...(currentPreferences.availabilities ?? []), ...(parsedPrefs.availabilities ?? [])])
            : null,
        isLgbtqia:
          parsedPrefs.isLgbtqia && !currentPreferences.isLgbtqia.includes(MEMBER_PREFERENCE_IDS['LGBTQIA+'])
            ? [...currentPreferences.isLgbtqia, MEMBER_PREFERENCE_IDS['LGBTQIA+']]
            : currentPreferences.isLgbtqia,
        isExperiencedWithVeterans:
          parsedPrefs.isExperiencedWithVeterans &&
          !currentPreferences.isExperiencedWithVeterans.includes(MEMBER_PREFERENCE_IDS.EXPERIENCE_WORKING_WITH_VETERANS)
            ? [...currentPreferences.isExperiencedWithVeterans, MEMBER_PREFERENCE_IDS.EXPERIENCE_WORKING_WITH_VETERANS]
            : currentPreferences.isExperiencedWithVeterans,
        meetingSetting: uniq([
          ...currentPreferences.meetingSetting,
          ...(parsedPrefs.preferredAppointmentType ?? []),
          ...(parsedPrefs.preferredSessionFormat ?? []),
        ]),
      }

      // Update local state to reflect merged preferences in accordions
      setCurrentPreferences(updatedPreferences)

      // Save the merged preferences to backend
      await onProviderPreferenceChange(updatedPreferences)
    }
    setIsCustomPreferencesLoading(false)

    // Check for high risk and trigger appropriate flow
    if (riskLevel === GenAiRiskLevel.RISK_LEVEL_YELLOW) {
      onGenAiHighRiskDetected()
    } else {
      setViewMatchedPreferences(true)
    }
  }

  const onMeetMatches = () => {
    setIsCustomPreferencesModalVisible(false)
    setViewMatchedPreferences(false)
    onConfirmPreferences?.()
  }

  // Hook for preference removal + persistence (shared with entry point flow)
  const removeMatchedPreference = useRemoveMatchedPreference({
    memberPreferencesV2,
    onSavePreferences: async (updatedPreferences) => {
      await saveMemberPreferenceForUserV2({ lyraId: userId, data: updatedPreferences })
      setUserMemberPreferencesV2?.(updatedPreferences)
    },
    setCurrentPreferences,
  })

  const onRemoveParsedPreference = ({
    preferenceKey,
    valueId,
  }: {
    preferenceKey: ParsedPreferenceKey
    valueId: string | undefined
  }) => {
    // Update modal display state (not handled by hook)
    const updatedMatchedPreferences = removePreferenceValueFromParsedPreferences(
      matchedCustomPreferences,
      preferenceKey,
      valueId,
    )
    setParsePreferenceData?.({
      data: updatedMatchedPreferences,
      id: parsePreferenceId ?? null,
      riskLevel: parsePreference?.riskLevel ?? null,
    })
    // Delegate accordion update + persistence to shared hook
    removeMatchedPreference({ preferenceKey, valueId })
  }

  const onBackToInput = () => {
    setViewMatchedPreferences(false)
    setIsCustomPreferencesError(false)
  }

  const onCancelCustomPreferencesModal = () => {
    // Ignore cancel if we're reopening after high risk dismissal (prevents race condition with modal close animation)
    if (isReopeningAfterHighRisk) {
      return
    }

    // Clear parse preference data when modal closes to ensure clean state for next open
    setParsePreferenceData?.({ data: {}, id: null, riskLevel: null })

    setIsCustomPreferencesModalVisible(false)
    setViewMatchedPreferences(false)
  }

  // Group all custom preferences modal props for easier passing to components
  const customPreferencesModalProps: CustomPreferencesModalHookProps = {
    isVisible: isCustomPreferencesModalVisible,
    onCancel: onCancelCustomPreferencesModal,
    onSubmit: onSubmitCustomPreferences,
    matchedCustomPreferences,
    isCustomPreferencesLoading,
    isCustomPreferencesError,
    onMeetMatches,
    onRemoveParsedPreference,
    viewMatchedPreferences,
    onBackToInput,
    isHighRiskModalVisible,
    onDismissHighRiskModal,
    isHighRisk,
    hasAcknowledgedHighRisk,
  }

  return {
    isHowPreferencesWorkModalVisible,
    setIsHowPreferencesWorkModalVisible,
    isResetDisabled,
    setResetDisabled,
    memberPreferencesMetadata,
    isSavingPreferences,
    currentPreferences,
    setCurrentPreferences,
    onProviderPreferenceChange,
    onPressResetButton,
    setIsCustomPreferencesModalVisible,
    customPreferencesModalProps,
  }
}
